import seeder from '@cleverbeagle/seeder';
import { Meteor } from 'meteor/meteor';
import { cloneDeep, forEach, find, isEmpty } from 'lodash';
import Layers from '../../api/Layers/Layers';
import layerExamples from './fixtures-examples/layers';
import joinLayerExamples from './fixtures-examples/layers/joinLayers';
import Maps from '../../api/Maps/Maps';
import Styles from '../../api/Styles/Styles';
import styleExamples from './fixtures-examples/styles';
import * as mapOps from '../../api/Maps/map-operations';
import * as layerOps from '../../api/Layers/layer-operations';
import * as styleOps from '../../api/Styles/style-operations';
import MapServerElements from '../../api/MapServerElements/MapServerElements';
import Connectors from '../../api/Connectors/Connectors';
import Sources from '../../api/Sources/Sources';
import * as sourceOps from '../../api/Sources/source-operations';
import * as connectorOps from '../../api/Connectors/connector-operations';
import * as mapServerElementOps from '../../api/MapServerElements/map-server-element-operations';
import mapExamples from './fixtures-examples/maps';
import mapServerElementsExamples from './fixtures-examples/mapServerElements';
import connectorsExamples from './fixtures-examples/connectors';
import sourcesExamples from './fixtures-examples/sources';
import AISettings from '../../api/AISettings/AISettings';
import AISettingsExamples from './fixtures-examples/aiSettings';

const adminRoles = ['admin'];

const addOwnerForStaticData = (staticData, userId) => {
  const examplesWithOwner = staticData.map(item => {
    const itemWithID = cloneDeep(item);
    itemWithID.owner = userId;
    return itemWithID;
  });
  return examplesWithOwner;
};

const replaceStylesWithIds = (layers, userId) => {
  const userStyles = Styles.find({ owner: userId }).fetch();
  const layersWithStylesIds = layers.map(layer => {
    const layerWidthId = cloneDeep(layer);
    if (!layerOps.isThematic(layer)) {
      const styleName = layerOps.getStyleId(layer);
      const style = find(userStyles, ['name', styleName]);
      const styleId = styleOps.getId(style);
      layerWidthId.styleId = styleId;
    }
    return layerWidthId;
  });

  return layersWithStylesIds;
};

const replaceLayersWithIds = (maps, userId) => {
  const userLayers = Layers.find({ owner: userId }).fetch();
  const mapsWithLayerIds = maps.map(map => {
    const mapWithNewLayerIds = cloneDeep(map);
    const mapLayerNames = mapOps.getLayers(mapWithNewLayerIds);
    const mapBaseMapNames = mapOps.getBaseMaps(mapWithNewLayerIds);
    const mapBaseMapTransparencyNames = mapOps.getBaseMapsTransparency(mapWithNewLayerIds);
    const mapLayerTransparencyNames = mapOps.getLayersTransparency(mapWithNewLayerIds);
    const mapLayerIds = [];
    const mapBaseMapIds = [];
    const mapBaseMapTransparencyIds = [];
    const mapLayerTransparencyIds = [];
    forEach(mapLayerNames, layerName => {
      const userLayer = find(userLayers, ['name', layerName]);
      mapLayerIds.push(layerOps.getId(userLayer));
    });
    forEach(mapBaseMapNames, baseMapName => {
      const userBaseMap = find(userLayers, ['name', baseMapName]);
      mapBaseMapIds.push(layerOps.getId(userBaseMap));
    });
    if (!isEmpty(mapBaseMapTransparencyNames)) {
      forEach(mapBaseMapTransparencyNames, baseMapName => {
        const userBaseMap = find(userLayers, ['name', baseMapName.id]);
        mapBaseMapTransparencyIds.push({
          id: layerOps.getId(userBaseMap),
          value: baseMapName.value
        });
      });
    }
    if (!isEmpty(mapLayerTransparencyNames)) {
      forEach(mapLayerTransparencyNames, layerName => {
        const userLayer = find(userLayers, ['name', layerName.id]);
        mapLayerTransparencyIds.push({
          id: layerOps.getId(userLayer),
          value: layerName.value
        });
      });
    }
    mapWithNewLayerIds.layers = mapLayerIds;
    mapWithNewLayerIds.baseMaps = mapBaseMapIds;
    mapWithNewLayerIds.baseMapsTransparency = mapBaseMapTransparencyIds;
    mapWithNewLayerIds.layersTransparency = mapLayerTransparencyIds;
    return mapWithNewLayerIds;
  });
  return mapsWithLayerIds;
};

const replaceLayersWithIdsInJoin = (joinLayers, userId) => {
  const userLayers = Layers.find({ owner: userId }).fetch();
  const joinLayersWithLayerIds = joinLayers.map(layer => {
    const joinLayerWithLayerIds = cloneDeep(layer);
    const geojsonLayerName = layerOps.getGeoJSONId(layer);
    const thematicLayerName = layerOps.getThematicId(layer);
    const geojsonLayer = find(userLayers, ['name', geojsonLayerName]);
    const thematicLayer = find(userLayers, ['name', thematicLayerName]);
    const geojsonId = layerOps.getId(geojsonLayer);
    const thematicId = layerOps.getId(thematicLayer);
    joinLayerWithLayerIds.geojsonId = geojsonId;
    joinLayerWithLayerIds.thematicId = thematicId;
    return joinLayerWithLayerIds;
  });

  return joinLayersWithLayerIds;
};

const replaceConnectorsWithIds = (sources, userId) => {
  const userConnectors = Connectors.find({ owner: userId }).fetch();

  const sourcesWithConnectorIds = sources.map(source => {
    const sourcesWidthId = cloneDeep(source);
    const connectorName = sourceOps.getConnectorId(source);
    const connector = find(userConnectors, ['name', connectorName]);
    const connectorId = connectorOps.getId(connector);
    sourcesWidthId.connectorId = connectorId;
    return sourcesWidthId;
  });

  return sourcesWithConnectorIds;
};

const replaceSourcesWithIds = (mapServerElements, userId) => {
  const userSources = Sources.find({ owner: userId }).fetch();

  const mapServerElementsWithConnectorIds = mapServerElements.map(mapServerElement => {
    const mapServerElementWithId = cloneDeep(mapServerElement);
    const sourceName = mapServerElementOps.getSourceId(mapServerElement);
    if (!isEmpty(sourceName)) {
      const source = find(userSources, ['name', sourceName]);
      const sourceId = sourceOps.getId(source);
      mapServerElementWithId.sourceId = sourceId;
    }
    return mapServerElementWithId;
  });

  return mapServerElementsWithConnectorIds;
};

const seedLayers = (userId, resetCollection = false) => {
  const layersWithStylesIds = replaceStylesWithIds(layerExamples, userId);
  seeder(Layers, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(layersWithStylesIds, userId)
    }
  });
};

const seedJoinLayers = userId => {
  const joinLayersWithLayerIds = replaceLayersWithIdsInJoin(joinLayerExamples, userId);
  const joinLayersWithStylesIds = replaceStylesWithIds(joinLayersWithLayerIds, userId);
  seeder(Layers, {
    resetCollection: false,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(joinLayersWithStylesIds, userId)
    }
  });
};

const seedAISettings = userId => {
  seeder(AISettings, {
    resetCollection: false,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(AISettingsExamples, userId)
    }
  });
};

const seedMaps = (userId, resetCollection = false) => {
  const mapsWithLayerIds = replaceLayersWithIds(mapExamples, userId);
  seeder(Maps, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(mapsWithLayerIds, userId)
    }
  });
};

const seedStyles = (userId, resetCollection = false) => {
  seeder(Styles, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(styleExamples, userId)
    }
  });
};

const seedMapServerElements = (userId, resetCollection = false) => {
  const mapServerElementWithIds = replaceSourcesWithIds(mapServerElementsExamples, userId);
  seeder(MapServerElements, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(mapServerElementWithIds, userId)
    }
  });
};

const seedConnectors = (userId, resetCollection = false) => {
  seeder(Connectors, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(connectorsExamples, userId)
    }
  });
};

const seedSources = (userId, resetCollection = false) => {
  const sourcesWithIds = replaceConnectorsWithIds(sourcesExamples, userId);
  seeder(Sources, {
    resetCollection,
    seedIfExistingData: true,
    environments: ['development', 'staging', 'production'],
    data: {
      static: addOwnerForStaticData(sourcesWithIds, userId)
    }
  });
};

seeder(Meteor.users, {
  resetCollection: false,
  seedIfExistingData: true,
  environments: ['development', 'staging'],
  data: {
    static: [
      {
        email: 'admin@maps.com',
        password: 'password',
        profile: {
          name: {
            first: 'Admin',
            last: 'User'
          }
        },
        roles: adminRoles,
        dependentData(userId) {
          seedStyles(userId, true);
          seedAISettings(userId, true);
          seedLayers(userId, true);
          seedJoinLayers(userId);
          seedMaps(userId, true);
          seedConnectors(userId, true);
          seedSources(userId, true);
          seedMapServerElements(userId, true);
        }
      }
    ],
    dynamic: {
      count: 0,
      seed(iteration, faker) {
        return {
          email: `user+${iteration + 1}@maps.com`,
          password: 'password',
          profile: {
            name: {
              first: faker.name.firstName(),
              last: faker.name.lastName()
            }
          },
          roles: ['free-user'],
          dependentData(userId) {
            seedStyles(userId);
            seedLayers(userId);
            seedJoinLayers(userId);
            seedMaps(userId);
            seedConnectors(userId);
            seedSources(userId);
            seedMapServerElements(userId);
          }
        };
      }
    }
  }
});

seeder(Meteor.users, {
  seedIfExistingData: true,
  environments: ['production'],
  data: {
    static: [
      {
        email: 'admin@maps.com',
        password: 'srmConsulting010',
        profile: {
          name: {
            first: 'Admin',
            last: 'User'
          }
        },
        roles: adminRoles,
        dependentData(userId) {
          seedStyles(userId);
          seedAISettings(userId);
          seedLayers(userId);
          seedJoinLayers(userId);
          seedMaps(userId);
          seedConnectors(userId);
          seedSources(userId);
          seedMapServerElements(userId);
        }
      }
    ]
  }
});

const userExists = Meteor.users.findOne({ 'emails.0.address': 'admin@maps.com' });

if (!userExists.emails[0].verified) {
  Meteor.users.update(
    { _id: userExists._id },
    {
      $set: {
        'emails.0.verified': true,
        'storageCuota.sizeLimit': 10 ** 9
      }
    }
  );
}
